import React, {
	useRef,
	useState,
	forwardRef,
	useImperativeHandle,
	useCallback,
	useEffect
} from 'react';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { clientStore } from '../ClientStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { formatTimeAgo } from 'shared/utils';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { SkillDropdown } from 'components/layout/form-fields/inputs/SkillDropdown';
import { JobRoleDropdown } from 'components/layout/form-fields/inputs/JobRoleDropdown';
import MultiItemsList from 'ats/components/multi-items-list/MultiItemsList';

const validationSchema = Yup.object({
	technologies: Yup.array().of(Yup.object()),
	jobRoles: Yup.array().of(Yup.object()),
	additionalInfos: Yup.array().of(Yup.string())
});

function FormikSubmittingBridge({ isSubmitting, onSubmittingChange }) {
	useEffect(() => {
		onSubmittingChange?.(isSubmitting);
	}, [isSubmitting, onSubmittingChange]);
	return null;
}

const ClientInsightsFormInner = forwardRef(function ClientInsightsForm(
	{
		data,
		onSuccess,
		isEditing: isEditingProp,
		onIsEditingChange,
		onSubmittingChange
	},
	ref
) {
	const isControlled = typeof onIsEditingChange === 'function';
	const [internalEditing, setInternalEditing] = useState(false);
	const isEditing = isControlled ? Boolean(isEditingProp) : internalEditing;
	const setEditing = next => {
		if (isControlled) {
			onIsEditingChange(next);
		} else {
			setInternalEditing(next);
		}
	};

	const cancelEdit = useCallback(() => {
		if (isControlled) {
			onIsEditingChange(false);
		} else {
			setInternalEditing(false);
		}
	}, [isControlled, onIsEditingChange]);

	const formikRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			submit: () => formikRef.current?.submitForm(),
			cancelEdit
		}),
		[cancelEdit]
	);

	const initialData = {
		technologies: data?.technologies.map(({ technology }) => technology) || [],
		jobRoles: data?.roles.map(({ role }) => role) || [],
		additionalInfos: data?.additionalInfos || []
	};

	const handleSubmit = async values => {
		const result = await clientStore.update(data.id, {
			client: {
				additionalInfos: values.additionalInfos
			},
			technologies: values.technologies,
			jobRoles: values.jobRoles
		});
		if (result.status === 'success') {
			notificationStore.pushSuccessNotification(
				'Client Updated',
				'The Client has been successfully updated.'
			);
		} else {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update the Client'
			);
		}
	};

	return (
		<div className="ClientInsightsForm">
			{!isControlled && (
				<div className="client-form-header">
					<div className="is-flex gap-1">
						<UserProfileAvatar
							imageUrl={data?.updatedBy?.imageUrl}
							name={data?.updatedBy?.name}
						/>
						<span>
							Updated:{' '}
							{data?.updatedAt
								? formatTimeAgo(new Date(data?.updatedAt))
								: null}
						</span>
					</div>
					<div className="action-buttons">
						{isEditing ? (
							<>
								<button
									type="button"
									onClick={() => setEditing(false)}
									className="cancel-btn">
									Cancel
								</button>
								<button
									type="button"
									disabled={formikRef.current?.isSubmitting}
									onClick={() => formikRef.current.submitForm()}
									className="save-btn">
									Save
								</button>
							</>
						) : (
							<button
								type="button"
								onClick={() => setEditing(true)}
								className="edit-btn">
								<img src="/ats/icons/pencil-blue-icon.svg" alt="" />
							</button>
						)}
					</div>
				</div>
			)}
			{isEditing ? (
				<Formik
					innerRef={formikRef}
					initialValues={initialData}
					validationSchema={validationSchema}
					onSubmit={async (values, actions) => {
						await handleSubmit(values);
						onSuccess && onSuccess();
						setEditing(false);
						actions.setSubmitting(false);
					}}>
					{({ setFieldValue, values, isSubmitting }) => (
						<Form className="client-form-content">
							<FormikSubmittingBridge
								isSubmitting={isSubmitting}
								onSubmittingChange={onSubmittingChange}
							/>
							{/* Technologies */}
							<div className="columns">
								<div className="column is-full">
									<div className="field has-text-left">
										<label className="subtitle is-7">Technologies</label>
										<div className="control">
											<SkillDropdown
												value={values.technologies}
												placeholder=" "
												styleVersion="ats"
												onValueChange={value => {
													setFieldValue('technologies', value || []);
												}}
											/>
										</div>
										<ErrorMessage
											name="technologies"
											component="div"
											className="text-red-600 text-sm"
										/>
									</div>
								</div>
							</div>

							{/* Job Roles */}
							<div className="columns">
								<div className="column is-full">
									<div className="field has-text-left">
										<label className="subtitle is-7">Job Roles</label>
										<div className="control">
											<JobRoleDropdown
												value={values.jobRoles}
												placeholder=" "
												styleVersion="ats"
												onValueChange={value => {
													setFieldValue('jobRoles', value || []);
												}}
											/>
										</div>
										<ErrorMessage
											name="jobRoles"
											component="div"
											className="text-red-600 text-sm"
										/>
									</div>
								</div>
							</div>

							{/* Additional Infos */}
							<div className="columns">
								<div className="column is-full">
									<div className="field has-text-left">
										<label className="subtitle is-7">Additional Info</label>
										<FieldArray name="additionalInfos">
											{({ push, remove, replace }) => (
												<MultiItemsList
													items={values.additionalInfos}
													push={push}
													remove={remove}
													replace={replace}
													errorName="additionalInfos"
												/>
											)}
										</FieldArray>
									</div>
								</div>
							</div>
						</Form>
					)}
				</Formik>
			) : (
				<div className="client-info-content">
					<div className="client-info-container">
						<h3>Technologies</h3>
						<div className="tags-container">
							{initialData.technologies.map(technology => (
								<span key={technology.id}>{technology.name}</span>
							))}
						</div>
					</div>

					<div className="client-info-container">
						<h3>Job Roles</h3>
						<div className="tags-container">
							{initialData.jobRoles.map(jobRole => (
								<span key={jobRole.id}>{jobRole.name}</span>
							))}
						</div>
					</div>

					<div className="client-info-container">
						<h3>Additional Info</h3>
						<ul>
							{initialData.additionalInfos.map((addInfo, i) => (
								<li key={i}>{addInfo}</li>
							))}
						</ul>
					</div>
				</div>
			)}
		</div>
	);
});

const ClientInsightsForm = observer(ClientInsightsFormInner);
export default ClientInsightsForm;
